/*
 * Copyright (c) 2024, King Living
 * @override - King Living registration fields with enhanced validation and password rules.
 *
 * Validation Rules:
 * - Email: Valid format, character limit
 * - FirstName/LastName: Alpha only, apostrophes allowed, no numbers/emojis/special chars
 * - Password: rules from SFCC Customer Login Settings (kl-customers), message from BM site preference
 */

import {useIntl} from 'react-intl';

import {usePasswordPolicy} from '@amblique/king-living/app/hooks/use-password-policy';
import {usePasswordValidationMessages} from '@amblique/king-living/app/hooks/use-password-validation-messages';
import {
  createPasswordValidateRules,
} from '@amblique/king-living/app/utils/password-validation-messages';
import {
  FIELD_LIMITS,
  sanitizeNameNumbers,
  validateEmail,
  validateName,
} from '@amblique/king-living/app/utils/validation';


export default function useRegistrationFields({
  form: {
    control,
    formState: {errors},
  },
  prefix = '',
}) {
  const {formatMessage} = useIntl();
  const {policy} = usePasswordPolicy();
  const {message: passwordValidationMessage} = usePasswordValidationMessages('registration');
  const firstNameLabel = formatMessage({
    defaultMessage: 'First name',
    id: 'use_registration_fields.label.first_name',
  });
  const lastNameLabel = formatMessage({
    defaultMessage: 'Last name',
    id: 'use_registration_fields.label.last_name',
  });

  const fields = {
    firstName: {
      name: `${prefix}firstName`,
      label: firstNameLabel,
      type: 'text',
      autoComplete: 'given-name',
      defaultValue: '',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your first name.',
          id: 'use_registration_fields.error.required_first_name',
        }),
        validate: (value) => validateName(value, {
          maxLength: FIELD_LIMITS.firstName,
          fieldName: firstNameLabel,
          formatMessage,
        }),
      },
      inputProps: ({onChange}) => ({
        maxLength: FIELD_LIMITS.firstName,
        onChange(evt) {
          onChange(sanitizeNameNumbers(evt.target.value));
        },
      }),
      error: errors[`${prefix}firstName`],
      control,
    },
    lastName: {
      name: `${prefix}lastName`,
      label: lastNameLabel,
      type: 'text',
      autoComplete: 'family-name',
      defaultValue: '',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your last name.',
          id: 'use_registration_fields.error.required_last_name',
        }),
        validate: (value) => validateName(value, {
          maxLength: FIELD_LIMITS.lastName,
          fieldName: lastNameLabel,
          formatMessage,
        }),
      },
      inputProps: ({onChange}) => ({
        maxLength: FIELD_LIMITS.lastName,
        onChange(evt) {
          onChange(sanitizeNameNumbers(evt.target.value));
        },
      }),
      error: errors[`${prefix}lastName`],
      control,
    },
    email: {
      name: `${prefix}email`,
      label: formatMessage({
        defaultMessage: 'Email address',
        id: 'use_registration_fields.label.email',
      }),
      type: 'email',
      autoComplete: 'email',
      defaultValue: '',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter a valid email address.',
          id: 'use_registration_fields.error.required_email',
        }),
        validate: (value) => validateEmail(value, {
          maxLength: FIELD_LIMITS.email,
          formatMessage,
        }),
      },
      inputProps: {
        maxLength: FIELD_LIMITS.email,
      },
      error: errors[`${prefix}email`],
      control,
    },
    password: {
      name: `${prefix}password`,
      label: formatMessage({
        defaultMessage: 'Password',
        id: 'use_registration_fields.label.password',
      }),
      type: 'password',
      autoComplete: 'new-password',
      defaultValue: '',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please create a password.',
          id: 'use_registration_fields.error.required_password',
        }),
        validate: createPasswordValidateRules({message: passwordValidationMessage, policy}),
      },
      error: errors[`${prefix}password`],
      control,
    },
    acceptsMarketing: {
      name: `${prefix}acceptsMarketing`,
      label: formatMessage({
        defaultMessage:
          'Sign me up for King newsletter to learn about our products and special offers. Opt out at any time.',
        id: 'use_registration_fields.label.sign_up_to_newsletter',
      }),
      type: 'checkbox',
      defaultValue: false,
      rules: {},
      error: errors[`${prefix}acceptsMarketing`],
      control,
    },
  };

  return fields;
}
